import { trackTabClick } from '@/shared/tracking/product-tab-clicked';
import { isDesktopView, scrollIntoView } from '@/shared/utils/dom';

const activeClassName = 'js-active';

export const initProductInfoTabs = () => {
  const tabsContainer =
    document.querySelector<HTMLDivElement>('.js-tabs-container');

  if (!tabsContainer) return;

  const tabButtons = Array.from(
    tabsContainer.querySelectorAll<HTMLButtonElement>('.js-tab-button')
  );
  const desktopTabs = tabButtons.filter((button) =>
    button.classList.contains('js-tab-button-desktop')
  );
  const mobileButtons = tabButtons.filter((button) =>
    button.classList.contains('js-tab-button-mobile')
  );
  const tabContents = Array.from(
    tabsContainer.querySelectorAll<HTMLElement>('.js-tab-content')
  );

  if (tabButtons.length === 0 || desktopTabs.length === 0) return;

  const syncAccessibilityState = () => {
    const activeContent = tabContents.find((content) =>
      content.classList.contains(activeClassName)
    );
    const activeName = activeContent?.id;

    desktopTabs.forEach((tab) => {
      const isActive = tab.name === activeName;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    mobileButtons.forEach((button) => {
      button.setAttribute('aria-expanded', String(button.name === activeName));
    });

    tabContents.forEach((content) => {
      const panel = content.querySelector<HTMLElement>(`#${content.id}-panel`);
      const desktopTab = desktopTabs.find((tab) => tab.name === content.id);
      if (!panel) return;

      if (isDesktopView() && desktopTab) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', desktopTab.id);
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
      }
    });
  };

  const activateTab = (name: string) => {
    tabContents.forEach((content) => {
      content.classList.toggle(activeClassName, content.id === name);
    });
    tabButtons.forEach((button) => {
      button.classList.toggle(activeClassName, button.name === name);
    });
    syncAccessibilityState();
  };

  tabButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const target = event.currentTarget as HTMLButtonElement;
      const content = tabContents.find((item) => item.id === target.name);
      if (!content) return;

      const wasActive = content.classList.contains(activeClassName);
      if (!wasActive) {
        trackTabClick(target.name);
      }

      if (!isDesktopView() && wasActive) {
        content.classList.remove(activeClassName);
        target.classList.remove(activeClassName);
        syncAccessibilityState();
      } else {
        activateTab(target.name);
      }

      if (!isDesktopView()) {
        window.setTimeout(() => scrollIntoView(target), 0);
      }
    });
  });

  desktopTabs.forEach((tab, index) => {
    tab.addEventListener('keydown', (event) => {
      let nextIndex: number | null = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (index + 1) % desktopTabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (index - 1 + desktopTabs.length) % desktopTabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = desktopTabs.length - 1;
          break;
      }

      if (nextIndex === null) return;
      event.preventDefault();
      const nextTab = desktopTabs[nextIndex];
      activateTab(nextTab.name);
      trackTabClick(nextTab.name);
      nextTab.focus();
    });
  });

  const ensureValidState = () => {
    const activeContent = tabContents.find((content) =>
      content.classList.contains(activeClassName)
    );
    const hasActiveDesktopTab = desktopTabs.some(
      (tab) => tab.name === activeContent?.id
    );

    if (isDesktopView() && !hasActiveDesktopTab) {
      activateTab(desktopTabs[0].name);
    } else {
      syncAccessibilityState();
    }
  };

  ensureValidState();
  window.addEventListener('resize', ensureValidState);
};
